/**
 * 合併預約與會員主檔。側欄 getAppointmentsByBrandUser 回傳的預約常缺 origin_id、branch_name 等，
 * 需從 members 列表或側欄 memberInfo 補齊（PopConfirmation 預覽、核銷結帳共用）。
 *
 * 合併後 `id` 為會員 ID（供 CustomerInfoSimple 等顯示用），預約 ID 保留在 `appointment_id`。
 * 診所欄位以預約的 branch_id 為準（會員主檔分店僅作 fallback）。
 * 諮詢師以「該筆預約」為準（可從 calendarRows 補齊），不沿用會員綁定諮詢師。
 */
import {
  resolveAppointmentCounsellorId,
  resolveAppointmentCounsellorName,
  resolveAppointmentType,
} from "@utils/brandUserAppointments";
export function resolveAppointmentId(appointment) {
  if (!appointment) return undefined;
  return appointment.appointment_id ?? appointment.id;
}

/** 比對月曆／列表列與選取預約是否同一筆（scheduler event id、appointment_id、合併會員後的 id） */
export function isSameAppointmentRow(row, selected) {
  if (!row || !selected) return false;
  const rowAptId = resolveAppointmentId(row);
  const selectedAptId = resolveAppointmentId(selected);
  if (
    rowAptId != null &&
    selectedAptId != null &&
    Number(rowAptId) === Number(selectedAptId)
  ) {
    return true;
  }
  if (
    row.id == null ||
    selected.id == null ||
    String(row.id) !== String(selected.id)
  ) {
    return false;
  }
  // id 相同但 appointment_id 不同（例如 memberInfo 殘留舊預約 id）→ 視為不同筆
  if (
    rowAptId != null &&
    selectedAptId != null &&
    Number(rowAptId) !== Number(selectedAptId)
  ) {
    return false;
  }
  return true;
}

export function resolveBranchNameFromList(branchId, branches = []) {
  if (branchId == null || !Array.isArray(branches) || branches.length === 0) {
    return undefined;
  }
  const hit = branches.find(
    (b) =>
      Number(b.id) === Number(branchId) ||
      Number(b.branch_id) === Number(branchId)
  );
  return hit?.branch_name ?? hit?.name ?? hit?.branchName;
}

export function mergeAppointmentWithMember(appointment, options = {}) {
  const { members = [], sidebarMemberInfo, branches = [] } = options;
  if (!appointment) return appointment;

  const memberFromList =
    members.find((m) => Number(m.id) === Number(appointment.user_id)) || {};

  const sidebarMember =
    sidebarMemberInfo &&
    (Number(sidebarMemberInfo.id) === Number(appointment.user_id) ||
      Number(sidebarMemberInfo.user_id) === Number(appointment.user_id))
      ? sidebarMemberInfo
      : {};

  const memberInfo = { ...sidebarMember, ...memberFromList };
  const appointmentId = resolveAppointmentId(appointment);

  const branch_id =
    appointment.branch_id != null
      ? appointment.branch_id
      : memberInfo.branch_id;

  const branch_name =
    appointment.branch_name ??
    resolveBranchNameFromList(appointment.branch_id, branches) ??
    memberInfo.branch_name ??
    resolveBranchNameFromList(memberInfo.branch_id, branches);

  const counsellor_id = resolveAppointmentCounsellorId(appointment, options);
  const counsellor_name = resolveAppointmentCounsellorName(
    counsellor_id != null
      ? { ...appointment, counsellor_id, counsellorId: counsellor_id }
      : appointment,
    options
  );
  const appointment_type = resolveAppointmentType(appointment, options);

  return {
    ...memberInfo,
    ...appointment,
    appointment_id: appointmentId,
    id: memberInfo.id ?? appointment.user_id,
    user_id: appointment.user_id ?? memberInfo.id,
    origin_id: appointment.origin_id || memberInfo.origin_id,
    user_phone:
      appointment.user_phone || memberInfo.phone || memberInfo.user_phone,
    phone: memberInfo.phone ?? appointment.user_phone,
    branch_id,
    branch_name,
    brand_member_id: memberInfo.brand_member_id ?? appointment.brand_member_id,
    brand_birthday:
      appointment.brand_birthday ||
      memberInfo.brand_birthday ||
      memberInfo.birthday,
    points_amount: memberInfo.points_amount ?? appointment.points_amount ?? 0,
    balance: memberInfo.balance ?? appointment.balance ?? 0,
    // 頭像 tag：等級／尾款以會員主檔為準（預約物件常缺或為 null 會蓋掉）
    memberLevelName:
      memberInfo.memberLevelName ??
      memberInfo.level_name ??
      memberInfo.member_level_name ??
      appointment.memberLevelName ??
      appointment.level_name ??
      appointment.member_level_name,
    level_name:
      memberInfo.level_name ??
      memberInfo.memberLevelName ??
      appointment.level_name,
    member_level_name:
      memberInfo.member_level_name ??
      memberInfo.memberLevelName ??
      appointment.member_level_name,
    hasDebt: memberInfo.hasDebt ?? appointment.hasDebt,
    counsellor_id,
    counsellorId: counsellor_id,
    counsellor_name,
    counsellorName: counsellor_name,
    ...(appointment_type.length > 0
      ? { appointment_type, appointmentType: appointment_type }
      : {}),
  };
}
